import React, { createContext, useContext, useState, useEffect } from 'react';

import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // ✅ REQUIRED: Separate async operations object
  const profileOperations = {
    async load(userId, authEmail = null) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
        
        if (!error && data) {
          // If auth email is provided and doesn't match profile email, update the profile
          if (authEmail && data.email !== authEmail) {
            console.warn(`Email mismatch detected: Profile has "${data.email}" but auth has "${authEmail}". Updating profile...`);
            try {
              const { data: updatedData, error: updateError } = await supabase
                ?.from('user_profiles')
                ?.update({ email: authEmail, updated_at: new Date().toISOString() })
                ?.eq('id', userId)
                ?.select()
                ?.single();
              
              if (!updateError && updatedData) {
                console.log('Profile email updated successfully');
                setUserProfile(updatedData);
              } else {
                console.error('Error updating profile email:', updateError);
                setUserProfile(data); // Use existing data even if update fails
              }
            } catch (updateErr) {
              console.error('Error updating profile email:', updateErr);
              setUserProfile(data); // Use existing data even if update fails
            }
          } else {
            setUserProfile(data);
          }
        } else if (error && error.code === 'PGRST116') {
          // Profile doesn't exist - this is OK, we'll use auth user data
          console.log('User profile not found in database, using auth user data only');
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setProfileLoading(false);
      }
    },
    
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // ✅ REQUIRED: Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        // Pass auth email to sync with profile if needed
        profileOperations?.load(session?.user?.id, session?.user?.email); // Fire-and-forget
      } else {
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  // Auth methods
  const signIn = async (email, password, redirectPath = null) => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // Trigger profile load and wait for it
      if (data?.user) {
        await profileOperations.load(data.user.id, data.user.email);
        // Wait a bit more to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      return { data, error: null, redirectPath };
    } catch (error) {
      console.error('Sign in error:', error);
      // Provide user-friendly error messages
      if (error.message?.includes('Email not confirmed') || error.message?.includes('email_not_confirmed')) {
        error.userMessage = 'Please check your email and click the confirmation link before signing in. If you didn\'t receive the email, please check your spam folder or contact support.';
      }
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, userData = {}) => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData?.fullName || '',
            role: userData?.role || 'tenant',
            ...userData
          }
        }
      });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    
    // Always clear local state first, even if server logout fails
    setUser(null);
    setUserProfile(null);
    
    try {
      // Try to sign out from Supabase, but don't fail if it errors
      // (session might already be invalid, or network issue)
      try {
        const { error } = await supabase?.auth?.signOut({
          scope: 'global' // Sign out from all sessions
        });
        
        if (error) {
          // Log but don't throw - we've already cleared local state
          console.warn('Supabase signOut returned error (non-critical):', error);
          // If it's a session missing error, that's fine - we're already logged out
          if (error.message?.includes('session') || 
              error.message?.includes('Auth session missing') ||
              error.message?.includes('403') ||
              error.message?.includes('400')) {
            console.log('Session already invalid or logout endpoint unavailable, continuing with logout');
          }
        }
      } catch (signOutError) {
        // Handle network errors or other exceptions during signOut
        console.warn('Exception during Supabase signOut (non-critical):', signOutError);
        // Continue with logout - local state is already cleared
      }
      
      return { error: null };
    } catch (error) {
      // Log but don't throw - we've already cleared local state
      console.warn('Sign out error (non-critical, local state cleared):', error);
      return { error: null }; // Return success since we cleared local state
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) {
      throw new Error('No authenticated user');
    }

    setProfileLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id)?.select()?.single();

      if (error) {
        throw error;
      }

      setUserProfile(data);
      return { data, error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { data: null, error };
    } finally {
      setProfileLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Password update error:', error);
      return { error };
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return userProfile?.role === role;
  };

  // Check if user is admin or manager
  const isAdmin = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'manager';
  };

  // Check if user is tenant
  const isTenant = () => {
    return userProfile?.role === 'tenant';
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    hasRole,
    isAdmin,
    isTenant,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;